/**
 * List of HTML tags allowed in chat messages by default.
 * Extend via the `extraAllowedTags` parameter of `sanitizeHtml` to support
 * additional tags in the future without modifying this file.
 */
export const ALLOWED_HTML_TAGS = ["br", "b", "ul", "li"] as const;
export type AllowedHtmlTag = (typeof ALLOWED_HTML_TAGS)[number];

/**
 * Sanitizes an HTML string, keeping only the allowed tags (stripped of all
 * attributes) and preserving text content.
 *
 * Disallowed elements are **unwrapped**: their inner content (including any
 * already-sanitized allowed children) is preserved, only the enclosing tag is
 * removed. Non-element, non-text nodes (e.g. HTML comments) are removed.
 *
 * @param html             Raw HTML string to sanitize.
 * @param extraAllowedTags Additional tag names to allow beyond the defaults.
 */
export function sanitizeHtml(html: string, extraAllowedTags: string[] = []): string {
  const allowedTags = new Set<string>([...ALLOWED_HTML_TAGS, ...extraAllowedTags]);
  const container = document.createElement("div");
  container.innerHTML = html;
  sanitizeElement(container, allowedTags);
  return container.innerHTML;
}

function sanitizeElement(element: Element, allowedTags: Set<string>): void {
  // Snapshot childNodes before iterating — DOM mutations during the loop would
  // otherwise cause skipped or double-processed nodes.
  const children = Array.from(element.childNodes);
  for (const node of children) {
    if (node.nodeType === Node.TEXT_NODE) {
      continue; // plain text — keep as-is
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      element.removeChild(node); // remove comments, CDATA, processing instructions, etc.
      continue;
    }
    const el = node as Element;
    if (!allowedTags.has(el.tagName.toLowerCase())) {
      // Disallowed tag: sanitize its children first, then unwrap (keep inner content).
      sanitizeElement(el, allowedTags);
      while (el.firstChild) {
        element.insertBefore(el.firstChild, el);
      }
      element.removeChild(el);
    } else {
      // Allowed tag: strip every attribute for safety, then recurse.
      Array.from(el.attributes).forEach((attr) => el.removeAttribute(attr.name));
      sanitizeElement(el, allowedTags);
    }
  }
}
